import { z } from 'zod';
import { promotionPriceDiscountTypeSchema } from '../../promotion/promotion-primitives.schema.js';
import { befretDeliveryMethodSchema, dpdServiceTypeSchema, shipmentCategoryForWeight, } from '../shared/shipment-category.js';
import { normalizeBusinessPhone, normalizeDialCode } from '../../phone/phone-number.js';
import { supportedLanguageSchema } from '../shared/shipment-primitives.schema.js';
import { userLocationInputSchema } from './user-location.schema.js';
import { roundWeightKg } from '../../pricing/final-price.js';
/**
 * Indicatif du contrat de création d'envoi.
 *
 * Les formulaires envoient indifféremment « +32 », « BE » ou « Belgium (+32) » :
 * le destinataire du tunnel porte le CODE ISO du sélecteur téléphonique
 * (`phonePrefix3`), l'expéditeur son INDICATIF (`phonePrefix2`). On normalise
 * seulement l'indicatif ici ; la validité du numéro complet relève du domaine
 * téléphone, et la sortie est jugée ensuite par `^\+\d{1,4}$`.
 *
 * #1728 — UN SEUL normaliseur : la règle est `normalizeDialCode`
 * (`@befret/core/phone`, libphonenumber, toutes régions). L'ancienne résolution
 * ISO passait par une table des seuls pays d'ORIGINE + `CD` : un destinataire au
 * Maroc, aux États-Unis ou au Cameroun — pays que le sélecteur propose — gardait
 * « MA » / « US » / « CM » tel quel, et la commande était refusée APRÈS le clic
 * (« téléphone destinataire invalide »).
 *
 * ⛔ Ce qui n'était pas accepté ne le devient pas : les CHIFFRES NUS restent
 * transmis tels quels, donc refusés comme avant. `normalizeDialCode` y ajoute un
 * `+` sans juger le contenu (`'0032'` → `'+0032'`, `'0'` → `'+0'`) : les lui
 * confier aurait fait passer au contrat ce qui n'est pas un indicatif. Et un
 * code pays inconnu (`'XX'`) ressort tel quel, refusé comme avant.
 */
export function normalizePhonePrefix(value) {
    const trimmed = String(value || '').trim();
    if (!trimmed)
        return '';
    if (/^\d+$/.test(trimmed))
        return trimmed;
    return normalizeDialCode(trimmed) || trimmed;
}
function normalizeNationalPhoneNumber(value) {
    if (typeof value !== 'string')
        return value;
    // Les espaces et séparateurs d’affichage ne font pas partie de la valeur persistée.
    // Le préfixe international reste dans `prefix`, le numéro national ne contient que des chiffres.
    return value.trim().replace(/[\s().-]/g, '');
}
const requiredText = (message) => z.string().trim().min(1, message);
const optionalText = z.preprocess((value) => (typeof value === 'string' && value.trim() === '' ? undefined : value), z.string().trim().optional());
const optionalUppercaseText = z.preprocess((value) => (typeof value === 'string' && value.trim() === '' ? undefined : value), z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .optional());
function requiredNumberInput(label, positive = false) {
    // Les formulaires transmettent les nombres sous forme de chaînes ; une chaîne vide doit
    // rester absente afin que Zod signale un champ requis au lieu de la convertir en zéro.
    return z.preprocess((value) => {
        if (typeof value !== 'string')
            return value;
        const trimmed = value.trim();
        return trimmed === '' ? undefined : Number(trimmed);
    }, positive
        ? z
            .number({ required_error: `${label} requis`, invalid_type_error: `${label} invalide` })
            .finite(`${label} invalide`)
            .positive(`${label} doit être positif`)
        : z
            .number({ required_error: `${label} requis`, invalid_type_error: `${label} invalide` })
            .finite(`${label} invalide`));
}
function optionalNumberInput(schema) {
    return z.preprocess((value) => {
        if (value === null || value === undefined)
            return undefined;
        if (typeof value !== 'string')
            return value;
        const trimmed = value.trim();
        return trimmed === '' ? undefined : Number(trimmed);
    }, schema.optional());
}
const positiveNumber = (label) => requiredNumberInput(label, true);
const optionalNonNegativeNumber = optionalNumberInput(z.number().finite().nonnegative());
const optionalPositiveNumber = optionalNumberInput(z.number().finite().positive());
/** Adresse allow-listée acceptée depuis le formulaire, sans champ logistique serveur. */
export const draftAddressSchema = z
    .object({
    street: requiredText('Rue requise'),
    houseNumber: optionalText,
    city: requiredText('Ville requise'),
    zipCode: z.string().trim(),
    country: z
        .string()
        .trim()
        .regex(/^[A-Za-z]{2}$/, 'Code pays invalide')
        .transform((value) => value.toUpperCase()),
    coordinates: z
        .object({
        latitude: requiredNumberInput('Latitude'),
        longitude: requiredNumberInput('Longitude'),
    })
        .strict()
        .optional(),
    addressComplement: optionalText,
    inputMode: z.enum(['autocomplete', 'manual']).optional(),
})
    .strict();
/**
 * Téléphone normalisé sous la forme `{ prefix, number }`.
 *
 * #1268 — ce contrat NORMALISE, il ne durcit pas. Il nettoie l'indicatif et les
 * séparateurs, et conserve les deux garde-fous historiques (`^\+\d{1,4}$` sur
 * l'indicatif, `^\d{4,15}$` sur le numéro — `parseAndKeepRawInput` traduit les
 * lettres en chiffres, `0800FLOWERS`, ce qu'un numéro persisté ne doit jamais
 * contenir). La forme reste `{ prefix, number, whatsapp }` pour ne pas perturber
 * `buildShipmentDraft` ni `unifiedShipmentDraftSchema` ; `number` garde sa
 * graphie nationale, l'E.164 canonique s'obtenant par `draftPhoneToE164`.
 *
 * 🔴 **Pourquoi la règle stricte n'est PAS branchée ici — ne pas la rajouter
 * sans arbitrage produit.**
 *
 * Une première version de #1268 posait un `.superRefine()` appelant
 * `validateBusinessPhone` sur ce schéma. C'était sans danger tant que
 * `createShipmentDraftInputSchema` n'avait aucun consommateur — puis #1258
 * (merge `1ebeff8e`) l'a branché sur le chemin vivant, des DEUX côtés :
 *
 *   - serveur : `create-shipment-draft.ts` → `safeParse` puis
 *     `throw new HttpsError('invalid-argument', …)` ;
 *   - client  : `shipment-draft-form.adapter.ts` → `.parse()`, qui JETTE, appelé
 *     depuis `unified-shipment-migration.service.ts` sur le tunnel d'envoi.
 *
 * Or la mesure lecture seule du 2026-08-14 sur `befret-production` dit que la
 * règle stricte refuse **18,1 %** des téléphones expéditeur de `shipments` et
 * **19,6 %** des `pendingShipments` — et qu'elle **n'accepte rien** que la règle
 * actuelle refuse (gain nul sur sept mesures). Bloquer ici refuserait donc
 * environ **une commande sur cinq**, en amont de `createInvoice`, pour zéro
 * bénéfice mesurable. Ce n'est pas un durcissement, c'est une panne.
 *
 * La règle stricte vit donc là où elle ne peut avorter aucune commande :
 * normalisation via `draftPhoneToE164`, et observation via le log
 * `createinvoice.phone_not_e164` posé par `createInvoice`. Le jour où l'on
 * voudra resserrer, ce sera **sur ces logs**, comme décision produit explicite —
 * jamais comme effet de bord d'un schéma partagé.
 */
export const draftPhoneSchema = z
    .object({
    prefix: z
        .string()
        .transform(normalizePhonePrefix)
        .pipe(z.string().regex(/^\+\d{1,4}$/, 'Indicatif invalide')),
    number: z.preprocess(normalizeNationalPhoneNumber, z.string().regex(/^\d{4,15}$/, 'Numéro de téléphone invalide')),
    whatsapp: z.boolean().optional().default(false),
})
    .strict();
/**
 * E.164 canonique d'un téléphone de brouillon, ou `null` s'il est invalide.
 * Point d'entrée unique pour Twilio, DPD et toute persistance normalisée.
 */
export function draftPhoneToE164(phone) {
    return normalizeBusinessPhone({ prefix: phone.prefix, number: phone.number });
}
/** Identité, téléphone et adresse d’un expéditeur ou destinataire. */
export const draftContactSchema = z
    .object({
    name: requiredText('Nom requis'),
    email: optionalText.transform((value) => value?.toLowerCase()),
    phone: draftPhoneSchema,
    address: draftAddressSchema,
})
    .strict();
/**
 * #1706 — l’adresse de RUE du destinataire n’est exigée qu’en livraison à domicile.
 *
 * `pickupMethod` vaut `warehouse` par DÉFAUT dans le tunnel d’envoi
 * (`new-shipment.component.ts`) : en retrait au point relais, le formulaire
 * n’AFFICHE même pas le champ d’adresse, puisque le colis est retiré à
 * l’agence. Le contrat exigeait pourtant `street` sur toute adresse, si bien
 * qu’aucune commande en retrait ne pouvait atteindre le résumé ni le paiement
 * — « L’adresse du destinataire est invalide », signalé pendant l’UAT.
 *
 * Le portillon métier (`ShipmentValidationService`, section destinataire) porte
 * DÉJÀ la règle juste : l’adresse n’est réclamée que si
 * `pickupMethod === 'home_delivery'`. C’est le schéma qui s’aligne sur lui.
 *
 * ⛔ L’assouplissement est confiné au DESTINATAIRE. `draftAddressSchema` reste
 * strict pour l’EXPÉDITEUR (adresse de collecte réelle, DPD en a besoin) et
 * pour le POINT RELAIS. Aucune adresse de repli n’est fabriquée : on n’invente
 * pas une rue, on ne l’exige simplement pas quand elle n’a pas de sens.
 *
 * La VILLE reste obligatoire dans les deux modes : elle désigne l’agence de
 * destination (Kinshasa / Lubumbashi) et pèse sur le tarif.
 */
export const draftReceiverAddressSchema = draftAddressSchema.extend({
    street: optionalText,
});
/** Destinataire : même contrat que l’expéditeur, rue conditionnelle en plus. */
export const draftReceiverContactSchema = draftContactSchema.extend({
    address: draftReceiverAddressSchema,
});
/**
 * Règle UNIQUE de l’exigence d’adresse destinataire (#1706).
 *
 * Elle est appliquée à la frontière du formulaire
 * (`createShipmentDraftInputSchema`) ET à la forme persistée
 * (`unifiedShipmentDraftSchema`) : deux points d’application, une seule
 * décision, pour qu’un brouillon « livraison à domicile » ne puisse jamais
 * être écrit sans l’adresse qui permettra de le livrer.
 */
export function receiverStreetIsRequired(befretDeliveryMethod) {
    return befretDeliveryMethod === 'home_delivery';
}
/** Dimensions physiques strictement positives, exprimées dans l’unité du formulaire. */
export const draftDimensionsSchema = z
    .object({
    length: positiveNumber('Longueur'),
    width: positiveNumber('Largeur'),
    height: positiveNumber('Hauteur'),
})
    .strict();
/** Ligne de contenu déclarée par le client ; poids et valeur restent optionnels. */
export const draftParcelItemSchema = z
    .object({
    description: requiredText('Description de l’article requise'),
    quantity: positiveNumber('Quantité'),
    value: optionalNonNegativeNumber,
    weight: optionalPositiveNumber,
    category: optionalText,
})
    .strict();
/**
 * Aperçu d’une promotion affichée au client. Il est conservé pour audit mais
 * ne devient jamais une autorité tarifaire lors de la création serveur.
 */
export const draftPromotionPreviewSchema = z
    .object({
    promotionId: optionalText,
    code: optionalUppercaseText,
    title: z.string().trim(),
    type: z.enum(['code', 'automatic']),
    discountAmount: requiredNumberInput('Montant de réduction'),
    discountType: promotionPriceDiscountTypeSchema,
})
    .strict();
/** Point relais choisi par le client, avec uniquement les informations utiles au shipment. */
export const draftPickupPointSchema = z
    .object({
    id: requiredText('Identifiant du point relais requis'),
    name: z.string().trim(),
    address: draftAddressSchema,
    openingHours: z.record(z.string()).optional(),
    services: z.array(z.string().trim()).optional(),
    phone: optionalText,
    distance: optionalNonNegativeNumber,
    qualityScore: optionalNonNegativeNumber,
})
    .strict();
/**
 * Frontière entre le formulaire et le domaine. Seuls les champs nécessaires à la demande
 * sont acceptés : identité, tracking, paiement, statut et données DPD restent côté serveur.
 */
export const createShipmentDraftInputSchema = z
    .object({
    requestId: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9_-]{8,128}$/, 'requestId invalide'),
    sender: draftContactSchema,
    // #1706 — la rue du destinataire n'est exigée qu'en livraison à domicile,
    // ce que tranche le `superRefine` en bas de ce schéma.
    receiver: draftReceiverContactSchema,
    parcel: z
        .object({
        // #1752 — le poids qui entre dans le domaine est arrondi à 2 décimales
        // (`roundWeightKg`), avant la catégorisation et avant la tarification
        // serveur (`authoritativeStandardPrice`). Arrondir, jamais refuser :
        // `10,005` devient `10,01`. Un poids qui s'arrondit à 0 reste refusé.
        weightKg: positiveNumber('Poids')
            .transform((weight) => roundWeightKg(weight))
            .pipe(z.number().positive('Poids doit être positif')),
        dimensions: draftDimensionsSchema.optional(),
        description: requiredText('Description colis requise'),
        contentType: optionalText,
        declaredValue: optionalNonNegativeNumber,
        items: z
            .array(draftParcelItemSchema)
            .optional()
            .transform((items) => (items?.length ? items : undefined)),
        reinforcedPackaging: z.boolean().optional().default(false),
        fragile: z.boolean().optional().default(false),
        specialInstructions: optionalText,
    })
        .strict(),
    service: z
        .object({
        dpdServiceType: dpdServiceTypeSchema,
        befretDeliveryMethod: befretDeliveryMethodSchema,
        pickupPoint: draftPickupPointSchema.optional(),
        collectionDate: optionalText,
        collectionTimeWindow: optionalText,
        collectionInstructions: optionalText,
    })
        .strict(),
    preferences: z
        .object({
        language: supportedLanguageSchema.optional(),
        timezone: optionalText,
    })
        .strict()
        .optional(),
    promotionCodes: z
        .array(z
        .string()
        .trim()
        .transform((value) => value.toUpperCase()))
        .optional()
        .default([])
        .transform((codes) => [...new Set(codes.filter(Boolean))]),
    promotionPreview: z
        .array(draftPromotionPreviewSchema)
        .optional()
        .transform((preview) => (preview?.length ? preview : undefined)),
    clientQuotedPrice: optionalNonNegativeNumber,
    userLocation: userLocationInputSchema.optional(),
    /**
     * #2191 — l'envoi que celui-ci REMPLACE, après « Modifier ma commande ».
     * Ce n'est qu'une revendication du navigateur : `createShipmentDraft` ne
     * l'écrit sur le brouillon que s'il appartient au même compte.
     */
    replacesShipmentId: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9_-]{1,128}$/, 'replacesShipmentId invalide')
        .optional(),
})
    .strict()
    .superRefine((input, context) => {
    // #1706 — la rue du destinataire, exigée seulement en livraison à domicile.
    // Le contrôle est placé AVANT le garde-fou de poids : un envoi à domicile
    // sans adresse doit être refusé quelle que soit la suite du parcours, et le
    // chemin d'erreur reste `receiver.address.street` pour que l'adaptateur du
    // frontoffice continue de présenter `RECEIVER_ADDRESS_INVALID`.
    if (receiverStreetIsRequired(input.service.befretDeliveryMethod) &&
        !input.receiver.address.street) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['receiver', 'address', 'street'],
            message: 'Rue requise',
        });
    }
    // Les colis lourds suivent le parcours sur devis ; les informations de collecte DPD
    // ne sont donc obligatoires que pour les colis standard pris en charge immédiatement.
    const weightKg = input.parcel.weightKg;
    // Le schéma numérique porte déjà l'issue détaillée. Ne jamais appeler le
    // catégoriseur jetant tant que le poids n'est pas valide : `safeParse`
    // doit rendre un refus Zod, pas laisser échapper une exception utilisateur.
    if (!Number.isFinite(weightKg) || weightKg <= 0)
        return;
    const heavy = shipmentCategoryForWeight(weightKg) === 'heavy';
    if (!heavy && input.service.dpdServiceType === 'shop_pickup' && !input.service.pickupPoint) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['service', 'pickupPoint'],
            message: 'Point relais requis',
        });
    }
    if (!heavy && input.service.dpdServiceType === 'home_pickup' && !input.service.collectionDate) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['service', 'collectionDate'],
            message: 'Date de collecte requise',
        });
    }
});
